/**
 * System registry — the composition root's plug socket.
 *
 * Every subsystem (vegetation, weather, VFX, audio, HUD, content...) registers itself
 * here instead of being wired by hand in main.ts. This exists so that many people can
 * add systems in parallel without all of them editing the same file and colliding.
 *
 * Contract for a system:
 *  - `init` runs once, before the first frame. Add meshes to `ctx.scene` here.
 *  - `fixedUpdate` runs at the sim rate. Only implement it if you affect simulation.
 *  - `update` runs once per rendered frame, before the draw. Do visual work here.
 *  - Never mutate BikeState from a system; read it. The bike sim owns its own state.
 *  - Keep per-frame allocation at zero; this runs 120+ times a second.
 */

import type * as THREE from 'three';
import type { Terrain } from '../world/terrain';
import type { BikePhysics, BikeState, SimEvents } from '../bike/physics';
import type { ControlInput } from '../input/controls';

export interface SystemContext {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  terrain: Terrain;
  physics: BikePhysics;
  /** Convenience alias for physics.state. */
  bike: BikeState;
  events: SimEvents;
  /** Root element for DOM overlays. */
  dom: HTMLElement;
  /** Seconds since start (sim clock). */
  time: number;
}

export interface GameSystem {
  /** Unique, stable id. Also the name shown in the debug overlay's timing list. */
  readonly id: string;
  /** Lower runs first. Rendering-dependent systems should use >= 100. */
  readonly order?: number;
  init?(ctx: SystemContext): void | Promise<void>;
  fixedUpdate?(dt: number, ctx: SystemContext, input: ControlInput): void;
  update?(dt: number, ctx: SystemContext, alpha: number): void;
  dispose?(): void;
}

const registry: GameSystem[] = [];

/** Register a system. Call at module top level; import the module from `src/systems.ts`. */
export function registerSystem(sys: GameSystem): void {
  if (registry.some((s) => s.id === sys.id)) {
    console.warn(`[systems] duplicate system id "${sys.id}" ignored`);
    return;
  }
  registry.push(sys);
  registry.sort((a, b) => (a.order ?? 50) - (b.order ?? 50));
}

export function getSystems(): readonly GameSystem[] {
  return registry;
}

/** Per-system timings, for the debug overlay and the capture harness. */
export const systemTimings: Record<string, number> = {};

/**
 * A post-processing system sets `taken = true` once it owns the final draw to the
 * canvas; the main loop then skips its direct renderer.render() call. Lives here
 * rather than in main.ts so systems can claim it without importing the entry point.
 */
export const renderHook = { taken: false };

export async function initSystems(ctx: SystemContext): Promise<void> {
  for (const s of registry) {
    try {
      await s.init?.(ctx);
    } catch (e) {
      console.error(`[systems] init failed: ${s.id}`, e);
    }
  }
}

export function fixedUpdateSystems(dt: number, ctx: SystemContext, input: ControlInput): void {
  for (const s of registry) {
    if (!s.fixedUpdate) continue;
    try {
      s.fixedUpdate(dt, ctx, input);
    } catch (e) {
      console.error(`[systems] fixedUpdate failed: ${s.id}`, e);
    }
  }
}

export function updateSystems(dt: number, ctx: SystemContext, alpha: number): void {
  for (const s of registry) {
    if (!s.update) continue;
    const t0 = performance.now();
    try {
      s.update(dt, ctx, alpha);
    } catch (e) {
      console.error(`[systems] update failed: ${s.id}`, e);
    }
    systemTimings[s.id] = performance.now() - t0;
  }
}
